import React, { useState } from 'react'
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  MenuItem,
  Paper,
  TextField,
  Typography,
} from '@mui/material'
import type { Team } from '../../types/gotypes'

interface PipelineBillingCardProps {
  pipelineName: string
  /** The customer's budget teams; archived ones are not offered. */
  teams: Team[]
  /** The pipeline's billing team id; "" for none. */
  teamId: string
  /** The billed team's name as the pipeline read serves it (it may be archived). */
  teamName?: string
  /** The address the pipeline's batches are attributed to. */
  attributedEmail?: string
  busy: boolean
  onChange: (teamId: string) => Promise<unknown>
}

/**
 * The team a pipeline's AI spend is billed to (per-pipeline billing team).
 * Its connector's work is billed to the same team. A choice is confirmed and
 * applied at once, like the coding gate, never parked in the draft save bar.
 */
const PipelineBillingCard: React.FC<PipelineBillingCardProps> = ({
  pipelineName, teams, teamId, teamName, attributedEmail, busy, onChange,
}) => {
  const [target, setTarget] = useState<Team | null>(null)
  const open = teams.filter((t) => t.archived_at == null)
  const currentListed = open.some((t) => t.id === teamId)

  const handleConfirm = async () => {
    if (!target) return
    try {
      await onChange(target.id)
    } catch {
      // The hook already reported the error.
    } finally {
      setTarget(null)
    }
  }

  return (
    <Paper elevation={1} sx={{ p: 3, mb: 3 }} data-testid="autocoder-pipeline-billing">
      <Typography variant="h6" gutterBottom>
        Billing
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        The team whose AI budget pays for this pipeline&apos;s coding, and for the connector that
        feeds it.
      </Typography>
      <TextField
        select
        size="small"
        label="Billed to"
        value={currentListed ? teamId : ''}
        onChange={(e) => {
          const next = open.find((t) => t.id === e.target.value)
          if (next && next.id !== teamId) setTarget(next)
        }}
        disabled={busy || open.length === 0}
        sx={{ minWidth: 280 }}
        slotProps={{ select: { displayEmpty: true }, inputLabel: { shrink: true } }}
        data-testid="autocoder-pipeline-billing-select"
      >
        <MenuItem value="" disabled>
          {teamId && teamName ? `${teamName} (archived)` : 'Choose a team'}
        </MenuItem>
        {open.map((t) => (
          <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>
        ))}
      </TextField>
      {teamId ? (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }} data-testid="autocoder-pipeline-billing-address">
          Attributed as {attributedEmail}
        </Typography>
      ) : (
        <Alert severity="info" sx={{ mt: 2 }} data-testid="autocoder-pipeline-billing-legacy">
          No team is chosen, so this pipeline&apos;s work is billed to the customer&apos;s billing
          address{attributedEmail ? ` (${attributedEmail})` : ''} and counts against that
          address&apos;s team. Choose the team that pays.
        </Alert>
      )}
      {open.length === 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          This customer has no open budget teams yet. Create one on the Teams page.
        </Typography>
      )}

      <Dialog open={Boolean(target)} onClose={() => setTarget(null)}>
        <DialogTitle>Bill to {target?.name}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {`"${pipelineName}" — new coding batches, and those its connector sends, are charged to the ` +
              `${target?.name} team's AI budget from now on. Batches already queued keep the team they were billed to.`}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTarget(null)}>Cancel</Button>
          <Button color="info" onClick={handleConfirm} disabled={busy} data-testid="autocoder-pipeline-billing-confirm">
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  )
}

export default PipelineBillingCard
